import { useMutation } from '@apollo/client/react'
import {
  CommentOrgTicketDocument,
  ConfirmOrgTicketFixedDocument,
  CreateOrgTicketDocument,
  OrgTicketDocument,
  OrgTicketsDocument,
} from '@/graphql/generated/graphql'

/** Crea un ticket privado `origen='portal'` (tipos: incidencia | propuesta). */
export function useCreateOrgTicket() {
  const [mutate, { loading, error }] = useMutation(CreateOrgTicketDocument, {
    refetchQueries: [OrgTicketsDocument],
  })
  return {
    createTicket: async (input: {
      type: string
      businessId: string
      systemCode: string
      title: string
      description?: string
      metadataJson?: string
    }) => {
      const res = await mutate({ variables: { input } })
      return res.data?.createOrgTicket ?? null
    },
    creating: loading,
    error,
  }
}

/**
 * Comentario PÚBLICO del propietario en el hilo del ticket.
 *
 * NO refetchea `OrgTicket`: desde el goal portal-correcciones-5 el hilo se
 * pinta desde `orgTicketTimeline`, y quien refresca es la pantalla llamando a
 * `resetToLatest()` (vuelve a la página 1, que es la más reciente). Refrescar
 * también el detalle sería una segunda petición cuyo `comments` ya no pinta
 * nadie — el campo sigue en el contrato, su retirada es limpieza del goal 4.
 */
export function useCommentOrgTicket(code: string) {
  const [mutate, { loading, error }] = useMutation(CommentOrgTicketDocument)
  return {
    comment: async (text: string) => {
      const res = await mutate({ variables: { input: { code, text } } })
      return res.data?.commentOrgTicket ?? null
    },
    commenting: loading,
    error,
  }
}

/**
 * Confirmación de corrección (decisión 4 del goal): NO toca la máquina de
 * estados — deja columnas + comentario público automático + aviso al analista.
 * Idempotente server-side (la segunda confirmación es un no-op).
 */
export function useConfirmOrgTicketFixed(code: string) {
  const [mutate, { loading, error }] = useMutation(
    ConfirmOrgTicketFixedDocument,
    {
      refetchQueries: [{ query: OrgTicketDocument, variables: { code } }],
    },
  )
  return {
    confirmFixed: async () => {
      const res = await mutate({ variables: { code } })
      return res.data?.confirmOrgTicketFixed ?? null
    },
    confirming: loading,
    error,
  }
}
